import { useEffect } from 'react'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'
import { useBooleanVariationFromFeatureFlag } from 'hooks/useBooleanVariationFromFeatureFlag'
import { LLM_WORDCLOUD_FEATURE_FLAG } from 'app/featureFlags'
import { isLlmWordCloudUrlFlagSet } from 'utils/urlUtil'

import { useDashboardData, useDashboardWidgetSetting } from '../../../../../hooks/useDashboardState'
import { usePageConfigUser } from '../../../../../hooks/usePageConfig'
import { getWidgetForDashboard } from '../../../../../features/widget/asyncThunks'
import { toFilter } from '../../../../../utils/widgetUtil'
import { getEmojiCategory, getInsightCategory } from '../../../../../utils/viewOptionUtil'
import { createInsightCount } from '../../../../../utils/runSearchUtil'
import CountType from '../../../../../enums/CountType'
import { MeasureStrings } from '../../../../../enums/Measure'
import { useAppDispatch } from '../../../../../app/hooks'
import { WordCloudWidgetsType } from '../../types'
import { Dashboard } from '../../../../../schemas/DashboardSchema'
import { InsightCountOptions } from '../../../../../schemas/action/InsightCountOptionsSchema'
import { DEFAULT_MAX_EXACT_ITEM } from '../../utils'

interface RunSearchEffectProps {
  widgetId: number
  children: React.ReactElement
  measure: MeasureStrings
  hasShowPrimaryObject?: boolean
  hasEmojiCategory?: boolean
  imageAnalysis?: boolean
}

const createSearch = (
  widgetSetting: WordCloudWidgetsType,
  dashboardData: Dashboard,
  pageConfigUser: any,
  measure: MeasureStrings,
  hasShowPrimaryObject?: boolean,
  hasEmojiCategory?: boolean,
  imageAnalysis?: boolean
) => {
  const { selectedTopic, countType, precision, viewOption } = widgetSetting
  let showPrimaryTerms: boolean | undefined
  if ('showPrimaryTerms' in widgetSetting) {
    showPrimaryTerms = widgetSetting.showPrimaryTerms
  }
  const { timeZone } = pageConfigUser

  const filter = toFilter(widgetSetting, timeZone)
  const topicIds = [selectedTopic.topicId!]

  const categories = getInsightCategory(viewOption!)
  const hasImageAnalysis = imageAnalysis !== undefined
  const options: Partial<InsightCountOptions> = {
    exactCount: DEFAULT_MAX_EXACT_ITEM,
    isAuthorBased: countType === CountType.AUTHORS,
    ...(hasShowPrimaryObject && { showPrimaryObject: showPrimaryTerms }),
    ...(hasEmojiCategory && { emojiCategory: getEmojiCategory(viewOption) }),
    ...(hasImageAnalysis && { imageAnalysis })
  }

  const { id: dashboardId, name } = dashboardData
  return createInsightCount(
    dashboardId!,
    name!,
    precision!,
    topicIds,
    filter,
    measure,
    categories,
    options
  )
}

const RunSearchEffect = ({
  widgetId,
  children,
  measure,
  hasShowPrimaryObject,
  hasEmojiCategory,
  imageAnalysis
}: RunSearchEffectProps): React.ReactElement => {
  const widgetSetting = useDashboardWidgetSetting<WordCloudWidgetsType>(widgetId)
  const dashboardData = useDashboardData()
  const pageConfigUser = usePageConfigUser()
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp(widgetId)
  // Mirror monitor-app PR #62: the LLM path activates from EITHER the feature
  // flag OR an explicit `?llm_wordcloud=true` URL param (forwarded into the SPA
  // iframe src). The URL param is the manual-testing escape hatch that works
  // before the LaunchDarkly flag is provisioned.
  const llmWordCloudEnabled =
    useBooleanVariationFromFeatureFlag(LLM_WORDCLOUD_FEATURE_FLAG) || isLlmWordCloudUrlFlagSet()

  useEffect(() => {
    const search = createSearch(
      widgetSetting,
      dashboardData!,
      pageConfigUser,
      measure,
      hasShowPrimaryObject,
      hasEmojiCategory,
      imageAnalysis
    )

    dispatch(
      getWidgetForDashboard({
        searches: [search],
        widgetId: widgetSetting.widgetId!,
        timestamp: refreshTimestamp,
        llmWordCloud: llmWordCloudEnabled
      })
    )
  }, [
    widgetSetting,
    dashboardData,
    pageConfigUser,
    dispatch,
    measure,
    hasShowPrimaryObject,
    hasEmojiCategory,
    imageAnalysis,
    refreshTimestamp,
    llmWordCloudEnabled
  ])

  return children
}

export default RunSearchEffect
